import './xeroBillFieldsSection.scss';

import { DateTimeEditor, DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import { Text, Tooltip } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import moment from 'moment';
import { XeroContact } from 'pages/requestForm/data/xero/XeroContact';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import {
    getCanCreateContact,
    getFieldsApprovalPermissionBySystemPurpose,
    getRequestEditMode,
    RequestEditMode,
} from 'pages/requestForm/selectors/requestSelectors';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { useMount } from 'react-use';
import { ocrIsLoadingState } from 'shared/states';

import { changeXeroSupplier, editRequestField, showXeroContactCreationPopup } from '../../../actions';
import { useGetRecognizedOcrAttachment } from '../../../hooks/useGetRecognizedOcrAttachment';
import { useOcrStatus } from '../../../hooks/useOcrStatus';
import { getShowValidationErrors } from '../../../selectors/pageSelectors';
import { getXeroContext } from '../../../selectors/xero';
import { getDefaultDueDate } from '../../../utils/getDefaultDueDate';
import { SupplierDataProvider } from '../dataProviders/SupplierDataProvider';
import { XeroContactDetails } from '../XeroContactDetails/XeroContactDetails';
import { messages } from './XeroBillFieldsSection.messages';

const { xeroConstants, commonConstants } = constants;

const bem = bemFactory.block('reqf-xero-bill-fields-section');
const qa = bemFactory.qa('reqf-xero-bill-fields-section');

interface XeroBillFieldsSectionProps {
    className?: string;
    request: domain.XeroBillRequest;
}

export const XeroBillFieldsSection: FC<XeroBillFieldsSectionProps> = memo((props) => {
    const { className, request } = props;

    const dispatch = useDispatch();

    const [supplierFieldPermission, dateFieldPermission, dueDateFieldPermission, referenceFieldPermission] =
        useSelector((state) =>
            getFieldsApprovalPermissionBySystemPurpose(state, request, [
                domain.FieldSystemPurpose.XeroSupplier,
                domain.FieldSystemPurpose.XeroDate,
                domain.FieldSystemPurpose.XeroDueDate,
                domain.FieldSystemPurpose.XeroReference,
            ])
        );

    const { contact, reference, date, dueDate } = request.details;
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const canCreateContact = useSelector((state) => getCanCreateContact(state, request));
    const isEditorMode = editMode === RequestEditMode.Editor;
    const isApproverMode = editMode === RequestEditMode.Approver;
    const showValidationErrors = useSelector(getShowValidationErrors);
    const { defaultPaymentTerms } = useSelector(getXeroContext);

    const isInvalidInvoiceReference = Boolean(
        (reference?.length && (!reference?.trim() || reference.length > xeroConstants.INVOICE_REFERENCE_MAX_LENGTH)) ||
            (referenceFieldPermission.required && !reference)
    );

    const initialContact = useRef(contact);
    const [paymentTerm, setPaymentTerm] = useState<domain.XeroPaymentTerm | undefined>(
        contact?.paymentTerms?.bills ? contact.paymentTerms.bills : defaultPaymentTerms?.bills
    );

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    useOcrStatus(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const onCreateContact = useCallback(
        (text: string | null) => {
            dispatch(showXeroContactCreationPopup(text));

            return Promise.resolve(null);
        },
        [dispatch]
    );

    const onChangeSupplier = useCallback(
        (supplier: XeroContact | null) => {
            dispatch(changeXeroSupplier(supplier));

            if (date) {
                setPaymentTerm(supplier?.paymentTerms?.bills || defaultPaymentTerms?.bills);
            }
        },
        [date, defaultPaymentTerms, dispatch]
    );

    const onChangeDueDate = useCallback(
        (date: string | null) => {
            dispatch(editRequestField<domain.XeroBillDetails>('dueDate', date));
        },
        [dispatch]
    );

    const updateDueDate = useCallback(
        (newDate: string | null, newPaymentTerm: domain.XeroPaymentTerm | undefined) => {
            if (newDate && newPaymentTerm) {
                const dueDate = getDefaultDueDate(newDate, newPaymentTerm.day, newPaymentTerm.termType);

                onChangeDueDate(dueDate);
            }
        },
        [onChangeDueDate]
    );

    const onChangeDocumentDate = useCallback(
        (date: string | null) => {
            dispatch(editRequestField<domain.XeroBillDetails>('date', date));
            updateDueDate(date, paymentTerm);
        },
        [dispatch, updateDueDate, paymentTerm]
    );

    const onChangeReference = useCallback(
        (reference: string) => {
            if (commonConstants.ONLY_SPACES_REG_EXP.test(reference)) {
                return;
            }

            dispatch(editRequestField<domain.XeroBillDetails>('reference', reference));
        },
        [dispatch]
    );

    const supplierStaticValues = useMemo(
        () => (isApproverMode && initialContact.current ? [initialContact.current] : []),
        [isApproverMode]
    );

    const showContactOcrWarning = Boolean(ocrData?.document?.supplierIds && ocrData.document.supplierIds.length > 1);
    const showContactMissingWarning = showValidationErrors && !contact;
    const showDueDateWarning = ocrAttachment && moment(dueDate).isBefore(date);

    useMount(() => {
        updateDueDate(date, paymentTerm);
    });

    return (
        <div className={bem.add(className)()} data-qa={qa()}>
            <Field className={bem('field-item', 'contact')} required title={messages.contactFieldTitle}>
                <Tooltip
                    disabled={!showContactOcrWarning}
                    placement='top'
                    width='max-content'
                    activator={
                        <SupplierDataProvider staticValues={supplierStaticValues}>
                            <DropdownEditor
                                disabled={isEditorMode || ocrIsLoading || supplierFieldPermission.disabled}
                                warning={showContactOcrWarning && !showContactMissingWarning}
                                invalid={showContactMissingWarning}
                                qa={qa('supplier-editor')}
                                value={contact}
                                singleLinePerItem={false}
                                onChange={onChangeSupplier}
                                placeholder={messages.contactFieldPlaceholder}
                                canCreateItemFn={() => canCreateContact}
                                createItemDisplayTextFn={() => messages.addContactButton}
                                onCreateItem={onCreateContact}
                            />
                        </SupplierDataProvider>
                    }
                >
                    <Text spacing='8' font='body' fontSize='small'>
                        {messages.multipleContactsWarning}
                    </Text>
                </Tooltip>

                <XeroContactDetails contact={contact} />
            </Field>

            <Field className={bem('field-item')} required title={messages.dateFieldTitle}>
                <DateTimeEditor
                    qa={qa('date-editor')}
                    disabled={isEditorMode || ocrIsLoading || dateFieldPermission.disabled}
                    invalid={showValidationErrors && !date}
                    value={date}
                    onChange={onChangeDocumentDate}
                    placeholder={messages.dateFieldPlaceholder}
                />
            </Field>

            <Field className={bem('field-item')} required title={messages.dueDateFieldTitle}>
                <Tooltip
                    disabled={!showDueDateWarning}
                    placement='top'
                    width='max-content'
                    activator={
                        <DateTimeEditor
                            qa={qa('due-date-editor')}
                            warning={showDueDateWarning}
                            disabled={isEditorMode || ocrIsLoading || dueDateFieldPermission.disabled}
                            invalid={showValidationErrors && !dueDate}
                            value={dueDate}
                            onChange={onChangeDueDate}
                            placeholder={messages.dueDateFieldPlaceholder}
                        />
                    }
                >
                    <Text spacing='8' font='body' fontSize='small'>
                        {messages.earlyDueDateWarning}
                    </Text>
                </Tooltip>
            </Field>

            <Field
                className={bem('field-item')}
                required={referenceFieldPermission.required}
                title={messages.referenceFieldTitle}
            >
                <TextEditor
                    qa={qa('reference-editor')}
                    disabled={isEditorMode || ocrIsLoading || referenceFieldPermission.disabled}
                    invalid={showValidationErrors && isInvalidInvoiceReference}
                    maxLength={constants.xeroConstants.INVOICE_REFERENCE_MAX_LENGTH}
                    value={reference ?? ''}
                    onChange={onChangeReference}
                    placeholder={messages.referenceFieldPlaceholder}
                />
            </Field>
        </div>
    );
});

XeroBillFieldsSection.displayName = 'XeroBillFieldsSection';
